import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Destroyable } from '../../utils/mixins/destroyable.mixin';
import { UpscalingRockFile } from '../../core/models/upscaling-rock-files.model';
import { RockImagesService } from '../../services/rock-images/rock-images.service';
import { RockModelsService } from '../../services/rock-models/rock-models.service';
import { SimulationsService } from '../../services/simulations/simulations.service';
import { SnackbarService } from '../../services/snackbar.service';
import { LoadingService } from '../../services/loading.service';
import { RockThumbnailService } from '../../services/rock-thumbnail/rock-thumbnail.service';
import { RemoveExtensionPipe } from '../../common/pipes/remove-extension.pipe';
import { HttpHeaders } from '@angular/common/http';
import { COMPONENT_ID_HEADER } from '../../core/consts/api.const';
import { UpscalingRockFileModelDto } from '../../core/models/upscaling-rock-file-models.model';
import { SelectCapillaryPressurePopupComponent } from '../../common/popups/select-capillary-pressure-popup/select-capillary-pressure-popup.component';
import { SelectPermeabilityPopupComponent } from '../../common/popups/select-permeability-popup/select-permeability-popup.component';
import { concatMap, EMPTY, tap } from 'rxjs';
import {
  FluidFlowCapillaryPressureDto,
  FluidFlowAbsolutePermeabilityDto,
  FluidFlowModelCreatePayload,
  FluidFlowModelDetailDto,
  FluidFlowPhaseDefinitionDto,
} from '../../core/models/simulation.model';

interface DirectionInfo {
  direction: string;
  permeability: number | null;
  status: string;
}

interface FluidFlowPhase {
  phaseNumber: number;
  label: number;
  fraction: number;
  porosity: number;
  permeabilityValue: number | null;
  formationFactorValue: number | null;
  capillaryPressure: FluidFlowCapillaryPressureDto | null;
  absolutePermeability: FluidFlowAbsolutePermeabilityDto | null;
  color?: string;
}

@Component({
  selector: 'app-fluid-flow-definition',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatTableModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    RemoveExtensionPipe,
  ],
  templateUrl: './fluid-flow-definition.component.html',
  styleUrl: './fluid-flow-definition.component.scss',
})
export class FluidFlowDefinitionComponent extends Destroyable(Object) implements OnInit {
  private readonly componentId: string = 'fluid-flow-definition';
  isLoading = true;

  simulationId: string = '';
  simulationName: string = '';
  rockFileName: string = '';
  rockModelName: string = '';
  rockModelId: string = '';
  modelId: string | null = null;
  sourceModelId: string | null = null;
  isReadonly: boolean = false;

  rockFile: UpscalingRockFile | null = null;
  thumbnailUrl: string | null = null;

  directions: DirectionInfo[] = [];
  selectedDirection: string = '';

  fluidFlowNameForm: FormGroup;
  phases: FluidFlowPhase[] = [];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly dialog: MatDialog,
    private readonly rockImagesService: RockImagesService,
    private readonly rockModelsService: RockModelsService,
    private readonly simulationsService: SimulationsService,
    private readonly rockThumbnailService: RockThumbnailService,
    private readonly snackbarService: SnackbarService,
    private readonly loadingService: LoadingService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    super();
    this.fluidFlowNameForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(256), Validators.pattern(/^[a-zA-Z0-9_-]+$/)]],
    });
  }

  ngOnInit(): void {
    this.subscribeToLoadingState();
    this.route.queryParams.pipe(this.takeUntilDestroyed()).subscribe((params) => {
      this.simulationId = params['simulationId'] || '';
      this.simulationName = params['simulationName'] || '';
      this.rockFileName = params['rockFile'] || '';
      this.rockModelName = params['rockModel'] || '';
      this.rockModelId = params['rockModelId'] || '';
      this.modelId = params['modelId'] || null;
      this.sourceModelId = params['sourceModelId'] || null;
      this.isReadonly = !!this.modelId;

      if (this.simulationId && this.rockFileName) {
        if (this.modelId) {
          this.loadExistingModel(this.modelId, true);
        } else if (this.sourceModelId) {
          this.loadExistingModel(this.sourceModelId, false);
        } else {
          this.loadData();
        }
      } else {
        this.router.navigate(['/upscaling/fluid-flow-models']);
      }
    });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.thumbnailUrl) {
      this.rockThumbnailService.revokeThumbnailUrl(this.thumbnailUrl);
    }
  }

  getFileSizeDisplay(): string {
    if (!this.rockFile) return 'N/A';
    const x = this.rockFile.voxelsX || 0;
    const y = this.rockFile.voxelsY || 0;
    const z = this.rockFile.voxelsZ || 0;
    if (x && y && z) {
      return `${x}x${y}x${z}`;
    }
    return 'N/A';
  }

  getRockModelUrl(): string {
    if (!this.rockFileName || !this.rockModelId) return '#';
    const urlTree = this.router.createUrlTree(['/phase-definition'], {
      queryParams: { fileName: this.rockFileName, modelId: this.rockModelId },
    });
    return this.router.serializeUrl(urlTree);
  }

  getSimulationResultsUrl(): string {
    if (!this.simulationId) return '#';
    return this.router.serializeUrl(
      this.router.createUrlTree(['/simulation-results/kabs', this.simulationId]),
    );
  }

  isFieldDisabled(phase: FluidFlowPhase): boolean {
    return phase.porosity === 0 || phase.porosity === 1;
  }

  getDirectionLabel(dir: DirectionInfo): string {
    const permLabel = dir.permeability != null ? `${dir.permeability.toFixed(2)} mD` : '-';
    return `${dir.direction.toUpperCase()} (${permLabel})`;
  }

  isDirectionDisabled(dir: DirectionInfo): boolean {
    return dir.permeability != null && dir.permeability <= 0;
  }

  selectCapillaryPressure(phase: FluidFlowPhase): void {
    if (this.isReadonly || this.isFieldDisabled(phase)) return;

    const dialogData: any = { fileName: this.rockFileName };
    if (phase.capillaryPressure?.simulationId) {
      dialogData.preselection = {
        simulationId: phase.capillaryPressure.simulationId,
        direction: phase.capillaryPressure.direction,
      };
    }

    const dialogRef = this.dialog.open(SelectCapillaryPressurePopupComponent, {
      width: '95vw',
      maxWidth: '1600px',
      minWidth: '1100px',
      height: '95vh',
      maxHeight: '95vh',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        phase.capillaryPressure = {
          simulationId: result.pcSimulationId,
          simulationName: result.pcSimulationName,
          direction: result.pcDirection,
          cycle: result.pcCycles?.join('/') || '',
          interfactialTension: result.interfactialTension ?? 0,
          contactAnglePrimaryDrainage: result.contactAnglePrimaryDrainage ?? 0,
          contactAngleFirstImbibition: result.contactAngleFirstImbibition ?? 0,
        };
        this.phases = [...this.phases];
        this.cdr.detectChanges();
      }
    });
  }

  selectAbsolutePermeability(phase: FluidFlowPhase): void {
    if (this.isReadonly || this.isFieldDisabled(phase)) return;

    const dialogData: any = { fileName: this.rockFileName, mode: 'subsample' };
    if (phase.absolutePermeability?.simulationId) {
      dialogData.preselection = {
        simulationId: phase.absolutePermeability.simulationId,
        direction: phase.absolutePermeability.direction,
      };
    }

    const dialogRef = this.dialog.open(SelectPermeabilityPopupComponent, {
      width: '95vw',
      maxWidth: '1400px',
      minWidth: '1100px',
      maxHeight: '90vh',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        phase.absolutePermeability = {
          simulationId: result.simulationId,
          simulationName: result.simulationName,
          direction: result.direction,
          absolutePermeability: result.absolutePermeability ?? 0,
        };
        this.phases = [...this.phases];
        this.cdr.detectChanges();
      }
    });
  }

  getCapillaryPressureDisplay(phase: FluidFlowPhase): string {
    if (this.isFieldDisabled(phase)) return '-';
    if (phase.capillaryPressure) {
      return phase.capillaryPressure.simulationName || 'Selected';
    }
    return 'Select PC\nSimulation';
  }

  getAbsolutePermeabilityDisplay(phase: FluidFlowPhase): string {
    if (this.isFieldDisabled(phase)) return '-';
    if (phase.absolutePermeability) {
      return phase.absolutePermeability.simulationName || 'Selected';
    }
    return 'Select\nPermeability';
  }

  canSave(): boolean {
    if (!this.fluidFlowNameForm.valid) return false;
    if (!this.selectedDirection) return false;
    if (this.phases.length === 0) return false;

    const enabledPhases = this.phases.filter((p) => !this.isFieldDisabled(p));
    return enabledPhases.every((p) => p.capillaryPressure && p.absolutePermeability);
  }

  save(): void {
    if (!this.canSave()) {
      this.snackbarService.openCustom({
        message: 'Please fill in all required fields',
        title: 'Validation Error',
        type: 'error',
        action: 'Close',
      });
      return;
    }

    const payload: FluidFlowModelCreatePayload = {
      name: this.fluidFlowNameForm.value.name,
      direction: this.selectedDirection.toUpperCase(),
      phasesDefinition: this.phases.map((p) => {
        const phaseDef: FluidFlowPhaseDefinitionDto = {
          phaseNumber: p.phaseNumber,
          fraction: p.fraction,
          phasePorosity: p.porosity,
          phasePermeability: p.permeabilityValue ?? 0,
          phaseFormationFactor: p.formationFactorValue ?? 0,
          capillaryPressure: null!,
          absolutePermeability: null!,
        };

        if (p.capillaryPressure && !this.isFieldDisabled(p)) {
          phaseDef.capillaryPressure = {
            simulationId: p.capillaryPressure.simulationId,
            simulationName: p.capillaryPressure.simulationName,
            direction: p.capillaryPressure.direction,
            cycle: p.capillaryPressure.cycle,
            interfactialTension: p.capillaryPressure.interfactialTension,
            contactAnglePrimaryDrainage: p.capillaryPressure.contactAnglePrimaryDrainage,
            contactAngleFirstImbibition: p.capillaryPressure.contactAngleFirstImbibition,
          };
        }

        if (p.absolutePermeability && !this.isFieldDisabled(p)) {
          phaseDef.absolutePermeability = {
            simulationId: p.absolutePermeability.simulationId,
            simulationName: p.absolutePermeability.simulationName,
            direction: p.absolutePermeability.direction,
            absolutePermeability: p.absolutePermeability.absolutePermeability,
          };
        }

        return phaseDef;
      }),
    };

    this.loadingService.startLoading(this.componentId);
    this.simulationsService
      .createFluidFlowModel(this.simulationId, payload, {
        headers: new HttpHeaders({ [COMPONENT_ID_HEADER]: this.componentId }),
      })
      .pipe(
        tap(() => {
          this.snackbarService.openCustom({
            message: `Fluid flow model "${payload.name}" saved successfully`,
            title: 'Success',
            type: 'success',
            action: 'Close',
          });
        }),
        concatMap(() => {
          this.router.navigate(['/upscaling/fluid-flow-manager'], {
            queryParams: {
              simulationId: this.simulationId,
              simulationName: this.simulationName,
              rockFile: this.rockFileName,
              rockModel: this.rockModelName,
            },
          });
          return EMPTY;
        }),
        this.takeUntilDestroyed(),
      )
      .subscribe();
  }

  back(): void {
    this.navigateToManager();
  }

  cancel(): void {
    this.navigateToManager();
  }

  private navigateToManager(): void {
    this.router.navigate(['/upscaling/fluid-flow-manager'], {
      queryParams: {
        simulationId: this.simulationId,
        simulationName: this.simulationName,
        rockFile: this.rockFileName,
        rockModel: this.rockModelName,
      },
    });
  }

  private loadExistingModel(fluidFlowModelId: string, readonly: boolean): void {
    this.loadingService.startLoading(this.componentId);

    const options = {
      headers: new HttpHeaders({
        [COMPONENT_ID_HEADER]: this.componentId,
      }),
    };

    this.simulationsService.getSimulationById(this.simulationId).pipe(
      tap((sim) => {
        this.simulationName = sim.name;
        this.rockFileName = sim.fileName || this.rockFileName;

        this.directions = [];
        if (sim.results) {
          sim.results.forEach((r: any) => {
            const dir = r.direction?.toLowerCase();
            if (dir) {
              this.directions.push({
                direction: dir,
                permeability: r.result?.absolutePermeability ?? null,
                status: r.status || '—',
              });
            }
          });
        }

        this.loadThumbnail();
      }),
      concatMap(() => this.rockImagesService.getRockFileMetadata(this.rockFileName, 'Upscaling')),
      tap((metadata) => {
        this.rockFile = metadata as UpscalingRockFile;
      }),
      concatMap(() =>
        this.simulationsService.getFluidFlowModelById(this.simulationId, fluidFlowModelId, options)
      ),
      tap((model: FluidFlowModelDetailDto) => {
        if (readonly) {
          this.fluidFlowNameForm.patchValue({ name: model.name });
          this.fluidFlowNameForm.disable();
          this.selectedDirection = model.direction?.toLowerCase() || '';
        } else {
          const duplicateName = `${model.name}_duplicate`;
          this.fluidFlowNameForm.enable();
          this.fluidFlowNameForm.patchValue({ name: duplicateName });
          this.selectedDirection = model.direction?.toLowerCase() || '';
        }

        this.phases = model.phasesDefinition.map((phase) => {
          const color = this.calculatePhaseColor(phase.phaseNumber);
          return {
            phaseNumber: phase.phaseNumber,
            label: phase.phaseNumber,
            fraction: phase.fraction,
            porosity: phase.phasePorosity,
            permeabilityValue: phase.phasePermeability ?? null,
            formationFactorValue: phase.phaseFormationFactor ?? null,
            capillaryPressure: phase.capillaryPressure || null,
            absolutePermeability: phase.absolutePermeability || null,
            color,
          };
        });

        this.loadingService.stopLoading(this.componentId);
      }),
      this.takeUntilDestroyed(),
    ).subscribe({
      error: () => {
        this.loadingService.stopLoading(this.componentId);
        this.snackbarService.openCustom({
          message: 'Failed to load fluid flow model',
          title: 'Error',
          type: 'error',
          action: 'Close',
        });
      },
    });
  }

  private loadData(): void {
    this.loadingService.startLoading(this.componentId);

    const options = {
      headers: new HttpHeaders({
        [COMPONENT_ID_HEADER]: this.componentId,
      }),
    };

    this.simulationsService.getSimulationById(this.simulationId).subscribe({
      next: (sim) => {
        this.simulationName = sim.name;
        this.rockFileName = sim.fileName || this.rockFileName;
        const rockFileModelName = (sim as any).rockFileModelName;
        if (rockFileModelName) {
          this.rockModelName = rockFileModelName;
        }

        this.directions = [];
        if (sim.results) {
          sim.results.forEach((r: any) => {
            const dir = r.direction?.toLowerCase();
            if (dir) {
              this.directions.push({
                direction: dir,
                permeability: r.result?.absolutePermeability ?? null,
                status: r.status || '—',
              });
            }
          });
        }

        this.loadRockFileMetadata(options);
        this.loadThumbnail();
      },
      error: () => {
        this.loadingService.stopLoading(this.componentId);
        this.snackbarService.openCustom({
          message: 'Failed to load simulation details',
          title: 'Error',
          type: 'error',
          action: 'Close',
        });
      },
    });
  }

  private loadRockFileMetadata(options: any): void {
    this.rockImagesService
      .getRockFileMetadata(this.rockFileName, 'Upscaling')
      .pipe(
        tap((metadata) => {
          this.rockFile = metadata as UpscalingRockFile;
        }),
        concatMap(() => {
          if (this.rockModelName) {
            return this.rockModelsService.getUpscalingRockFileModels(this.rockFileName, options);
          }
          return EMPTY;
        }),
        this.takeUntilDestroyed(),
      )
      .subscribe({
        next: (models: any[]) => {
          const match = models.find((m: any) => m.name === this.rockModelName);
          if (match) {
            this.rockModelId = match.id;
            this.rockModelsService
              .getUpscalingRockFileModel(this.rockFileName, match.id, options)
              .pipe(this.takeUntilDestroyed())
              .subscribe({
                next: (model: UpscalingRockFileModelDto) => {
                  this.initializePhasesFromModel(model);
                  this.loadingService.stopLoading(this.componentId);
                },
                error: () => {
                  this.initializePhasesFromRockFile();
                  this.loadingService.stopLoading(this.componentId);
                },
              });
          } else {
            this.initializePhasesFromRockFile();
            this.loadingService.stopLoading(this.componentId);
          }
        },
        error: () => {
          this.initializePhasesFromRockFile();
          this.loadingService.stopLoading(this.componentId);
        },
      });
  }

  private initializePhasesFromModel(model: UpscalingRockFileModelDto): void {
    if (!model.phasesDefinition) {
      this.initializePhasesFromRockFile();
      return;
    }

    this.phases = model.phasesDefinition.map((phase: any) => {
      const phaseNumber = phase.phaseNumber ?? phase.label ?? phase.phase ?? 0;
      const label = phase.label ?? phaseNumber;
      const color = this.calculatePhaseColor(label);
      return {
        phaseNumber,
        label,
        fraction: phase.fraction ?? 0,
        porosity: phase.porosity ?? 0,
        permeabilityValue: phase.phasePermeability ?? null,
        formationFactorValue: phase.phaseFormationFactor ?? null,
        capillaryPressure: null,
        absolutePermeability: null,
        color,
      };
    });
  }

  private initializePhasesFromRockFile(): void {
    if (!this.rockFile) return;

    if (this.rockFile.fractionHistogram) {
      this.phases = Object.entries(this.rockFile.fractionHistogram).map(([label, fraction], index) => {
        const labelNum = parseInt(label, 10);
        const color = this.calculatePhaseColor(labelNum);
        return {
          phaseNumber: labelNum,
          label: labelNum,
          fraction,
          porosity: 0,
          permeabilityValue: null,
          formationFactorValue: null,
          capillaryPressure: null,
          absolutePermeability: null,
          color,
        };
      });
    }
  }

  private calculatePhaseColor(label: number | null | undefined): string {
    if (label === null || label === undefined || isNaN(label)) {
      return 'rgb(128, 128, 128)';
    }
    if (!this.rockFile) return 'rgb(128, 128, 128)';

    const labelStr = String(label);
    if (this.rockFile.normalizedPhases && this.rockFile.normalizedPhases[labelStr] !== undefined) {
      const normalizedValue = this.rockFile.normalizedPhases[labelStr];
      if (normalizedValue !== null && normalizedValue !== undefined && !isNaN(normalizedValue)) {
        return `rgb(${normalizedValue}, ${normalizedValue}, ${normalizedValue})`;
      }
    }
    if (this.rockFile.maxPhaseValue && this.rockFile.maxPhaseValue > 0) {
      const grayValue = Math.round((Number(label) / this.rockFile.maxPhaseValue) * 255);
      const clampedValue = Math.max(0, Math.min(255, grayValue));
      return `rgb(${clampedValue}, ${clampedValue}, ${clampedValue})`;
    }
    return 'rgb(128, 128, 128)';
  }

  private loadThumbnail(): void {
    this.rockThumbnailService
      .getThumbnail(this.rockFileName, 'Upscaling')
      .pipe(this.takeUntilDestroyed())
      .subscribe({
        next: (url) => {
          this.thumbnailUrl = url;
        },
        error: () => {
          this.thumbnailUrl = null;
        },
      });
  }

  private subscribeToLoadingState(): void {
    this.loadingService
      .getLoadingState(this.componentId)
      .pipe(this.takeUntilDestroyed())
      .subscribe((loadingState: boolean) => {
        this.isLoading = loadingState;
      });
  }
}
